import React, { useEffect, useState } from "react";
import {
  cargarCorrector,
  revisarTexto,
  aplicarCorreccionesTexto,
  agregarPalabraAlDiccionario,
} from "./ortografia";

const CLAVES_NO_TEXTO = ["logoCliente", "imagenBom"];

const recorrerYObtenerTextos = (valor, acumulador) => {
  if (typeof valor === "string") {
    acumulador.push(valor);
    return;
  }
  if (Array.isArray(valor)) {
    valor.forEach((item) => recorrerYObtenerTextos(item, acumulador));
    return;
  }
  if (valor && typeof valor === "object") {
    Object.keys(valor).forEach((clave) => {
      if (CLAVES_NO_TEXTO.includes(clave)) return;
      recorrerYObtenerTextos(valor[clave], acumulador);
    });
  }
};

const aplicarCorreccionesObjeto = (valor, correcciones) => {
  if (typeof valor === "string") {
    return aplicarCorreccionesTexto(valor, correcciones);
  }
  if (Array.isArray(valor)) {
    return valor.map((item) => aplicarCorreccionesObjeto(item, correcciones));
  }
  if (valor && typeof valor === "object") {
    const nuevo = {};
    Object.keys(valor).forEach((clave) => {
      nuevo[clave] = CLAVES_NO_TEXTO.includes(clave)
        ? valor[clave]
        : aplicarCorreccionesObjeto(valor[clave], correcciones);
    });
    return nuevo;
  }
  return valor;
};

const RevisionOrtografica = ({ abierto, datos, onConfirmar, onCancelar }) => {
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [hallazgos, setHallazgos] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [personalizando, setPersonalizando] = useState({});
  const [spellInstancia, setSpellInstancia] = useState(null);
  const [agregadas, setAgregadas] = useState({});

  useEffect(() => {
    if (!abierto || !datos) return undefined;
    let cancelado = false;
    setCargando(true);
    setErrorCarga("");
    setHallazgos([]);
    setSeleccion({});
    setPersonalizando({});
    setAgregadas({});
    setSpellInstancia(null);

    cargarCorrector()
      .then((spell) => {
        if (cancelado) return;
        setSpellInstancia(spell);
        const textos = [];
        recorrerYObtenerTextos(datos, textos);

        const mapa = new Map();
        textos.forEach((texto) => {
          revisarTexto(spell, texto).forEach(({ palabra, sugerencias }) => {
            if (!mapa.has(palabra)) mapa.set(palabra, sugerencias);
          });
        });

        const lista = Array.from(mapa, ([palabra, sugerencias]) => ({
          palabra,
          sugerencias,
        }));

        const seleccionInicial = {};
        lista.forEach(({ palabra, sugerencias }) => {
          seleccionInicial[palabra] = sugerencias[0] || palabra;
        });

        setHallazgos(lista);
        setSeleccion(seleccionInicial);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelado) {
          setErrorCarga(
            "No se pudo cargar el corrector ortográfico. Puedes continuar sin revisión."
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [abierto, datos]);

  if (!abierto) return null;

  const elegirOpcion = (palabra, valor) => {
    setSeleccion((prev) => ({ ...prev, [palabra]: valor }));
    setPersonalizando((prev) => ({ ...prev, [palabra]: false }));
  };

  const activarPersonalizado = (palabra) => {
    setPersonalizando((prev) => ({ ...prev, [palabra]: true }));
  };

  const escribirPersonalizado = (palabra, valor) => {
    setSeleccion((prev) => ({ ...prev, [palabra]: valor }));
  };

  const agregarAlDiccionario = (palabra) => {
    if (!spellInstancia) return;
    agregarPalabraAlDiccionario(spellInstancia, palabra);
    setAgregadas((prev) => ({ ...prev, [palabra]: true }));
    setSeleccion((prev) => ({ ...prev, [palabra]: palabra }));
    setPersonalizando((prev) => ({ ...prev, [palabra]: false }));
  };

  const confirmar = () => {
    const correcciones = new Map();
    hallazgos.forEach(({ palabra }) => {
      const valor = (seleccion[palabra] ?? palabra).trim();
      if (valor && valor !== palabra) correcciones.set(palabra, valor);
    });
    const datosCorregidos =
      correcciones.size > 0
        ? aplicarCorreccionesObjeto(datos, correcciones)
        : datos;
    onConfirmar(datosCorregidos);
  };

  return (
    <div className="docgen-revision-overlay" role="dialog" aria-modal="true">
      <div className="docgen-revision-modal">
        <div className="docgen-revision-header">
          <h2 className="docgen-revision-title">Revisión ortográfica</h2>
          <p className="docgen-revision-subtitle">
            Verifica el texto extraído del Assessment antes de generar el BOM.
          </p>
        </div>

        <div className="docgen-revision-body">
          {cargando && (
            <p className="docgen-revision-estado">Analizando el texto...</p>
          )}

          {!cargando && errorCarga && (
            <p className="docgen-revision-estado docgen-revision-estado--error">
              {errorCarga}
            </p>
          )}

          {!cargando && !errorCarga && hallazgos.length === 0 && (
            <p className="docgen-revision-estado">
              No se encontraron posibles errores ortográficos.
            </p>
          )}

          {!cargando && !errorCarga && hallazgos.length > 0 && (
            <ul className="docgen-revision-lista">
              {hallazgos.map(({ palabra, sugerencias }) => (
                <li key={palabra} className="docgen-revision-item">
                  <span className="docgen-revision-palabra">{palabra}</span>
                  <span className="docgen-revision-flecha" aria-hidden="true">
                    →
                  </span>

                  {agregadas[palabra] ? (
                    <span className="docgen-revision-agregada">
                      ✓ Añadida al diccionario — ya no se marcará como error
                    </span>
                  ) : (
                    <div className="docgen-revision-opciones">
                      {sugerencias.map((sugerencia) => (
                        <button
                          key={sugerencia}
                          type="button"
                          className={
                            "docgen-revision-opcion" +
                            (!personalizando[palabra] &&
                            seleccion[palabra] === sugerencia
                              ? " docgen-revision-opcion--activa"
                              : "")
                          }
                          onClick={() => elegirOpcion(palabra, sugerencia)}
                        >
                          {sugerencia}
                        </button>
                      ))}

                      <button
                        type="button"
                        className={
                          "docgen-revision-opcion docgen-revision-opcion--mantener" +
                          (!personalizando[palabra] &&
                          seleccion[palabra] === palabra
                            ? " docgen-revision-opcion--activa"
                            : "")
                        }
                        onClick={() => elegirOpcion(palabra, palabra)}
                      >
                        Dejar igual
                      </button>

                      {!personalizando[palabra] ? (
                        <button
                          type="button"
                          className="docgen-revision-opcion docgen-revision-opcion--manual"
                          onClick={() => activarPersonalizado(palabra)}
                        >
                          Escribir otra...
                        </button>
                      ) : (
                        <input
                          type="text"
                          className="docgen-revision-input"
                          autoFocus
                          defaultValue={
                            seleccion[palabra] === palabra ? "" : seleccion[palabra]
                          }
                          onChange={(event) =>
                            escribirPersonalizado(palabra, event.target.value)
                          }
                          placeholder="Escribe la corrección"
                        />
                      )}

                      <button
                        type="button"
                        className="docgen-revision-opcion docgen-revision-opcion--agregar"
                        onClick={() => agregarAlDiccionario(palabra)}
                        title="Esta palabra es correcta (nombre propio, término técnico, etc.) y no debe volver a marcarse."
                      >
                        + Añadir al diccionario
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="docgen-revision-footer">
          <button type="button" className="docgen-cancel-btn" onClick={onCancelar}>
            Cancelar
          </button>
          <button
            type="button"
            className="orgproc-card-btn"
            onClick={confirmar}
            disabled={cargando}
          >
            {hallazgos.length > 0
              ? "Aplicar correcciones y continuar →"
              : "Continuar →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionOrtografica;